<script setup lang="ts">
import equal from "deep-equal";
import moment from "moment";
import { computed, onUnmounted, ref } from "vue";

import actions from "~/actions";
import {
  getPropertyConfig,
  getPropertyDisplayTitle,
  getPropertyPartialTask,
  getPropertyValueFromTask,
} from "~/common/properties";
import DatePicker from "~/components/dumb/DatePicker.vue";
import DatesChip from "~/components/dumb/DatesChip.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { notify } from "~/components/notifications";
import { DatesFieldIcon, DueDateFieldIcon } from "~/icons";
import { CommandId, EditorMode, NotificationType, Placement } from "~/shared/enums";
import type { DateRange, PropertyAnyDates, Task } from "~/shared/types";
import { useAppStore, useDataStore } from "~/stores";
import { generateFieldRecommendation } from "~/utils/recommendation";
import { getFullDate } from "~/utils/time";

// TODO times add a time picker with a prop

const props = defineProps<{
  property: PropertyAnyDates;
  tasks: Task[];
  showIcon?: boolean;
  editorMode: EditorMode;
  value?: DateRange;
  defaultValue?: DateRange;
  onAfterClose?: (value: DateRange) => void;
}>();

const emit = defineEmits<{
  update: [range?: DateRange];
  reject: [];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();

const datePicker = ref<InstanceType<typeof DatePicker> | null>(null);
const feedbackTooltipNode = ref<HTMLDivElement | null>(null);

const propertyConfig = computed(() => getPropertyConfig(props.property.kind));
const isDefault = computed(() => propertyConfig.value.isDefault);
const isReadOnly = computed(() => propertyConfig.value.readOnly);

const loading = computed(() => isDefault.value && appStore.isLoadingTasksProperty(props.tasks, props.property.duid));

const isChipMode = computed(
  () => props.editorMode === EditorMode.CHIP || props.editorMode === EditorMode.CHIP_RECOMMENDATION
);
const isListMiniRoadmapMode = computed(() => props.editorMode === EditorMode.LIST_MINI_ROADMAP);
const isListMode = computed(() => props.editorMode === EditorMode.LIST || isListMiniRoadmapMode.value);
const isTaskDetailMode = computed(
  () => props.editorMode === EditorMode.DETAIL || props.editorMode === EditorMode.DETAIL_RECOMMENDATION
);
const isContextMenuMode = computed(() => props.editorMode === EditorMode.CONTEXT_MENU);
const isAutomationMode = computed(() => props.editorMode === EditorMode.AUTOMATION);
const isFormOrAutomationMode = computed(() => props.editorMode === EditorMode.FORM || isAutomationMode.value);

const isRange = computed(
  () =>
    ("adtl" in props.property && props.property.adtl.isRange) ||
    propertyConfig.value.alwaysShowForLayouts?.includes(appStore.layoutKind)
);

const selectedTasks = computed(() =>
  !isListMode.value
    ? props.tasks
    : dataStore.getTasksByDuidsOrderedIfLoaded([...appStore.selectedTaskDuids], {
        includeTrashed: false,
        includeDraft: false,
      })
);
const selectedTaskDuids = computed(() => selectedTasks.value.map((e) => e.duid));

const selectedDateRange = computed(() => {
  if (isFormOrAutomationMode.value) {
    return {
      start: props.value?.[0] ?? null,
      end: props.value?.[1] ?? null,
    };
  }

  const firstTaskValue = getPropertyValueFromTask(props.property, props.tasks[0]);
  if (typeof firstTaskValue === "string") {
    if (props.tasks.every((task) => getPropertyValueFromTask(props.property, task) === firstTaskValue)) {
      return {
        start: null,
        end: firstTaskValue,
      };
    }
    return { start: null, end: null };
  }

  if (
    (!isRange.value ||
      props.tasks.every((task) => getPropertyValueFromTask(props.property, task)?.[0] === firstTaskValue?.[0])) &&
    props.tasks.every((task) => getPropertyValueFromTask(props.property, task)?.[1] === firstTaskValue?.[1])
  ) {
    return {
      start: firstTaskValue?.[0] ?? null,
      end: firstTaskValue?.[1] ?? null,
    };
  }
  return { start: null, end: null };
});
const selectedRecurrence = computed(() => {
  if (isFormOrAutomationMode.value) {
    return { recurrence: null, recursNextAt: null };
  }

  if (
    isDefault.value &&
    props.tasks.every((task) => equal(task.recurrence, props.tasks[0].recurrence, { strict: true })) &&
    props.tasks.every((task) => task.recursNextAt === props.tasks[0].recursNextAt)
  ) {
    return {
      recurrence: props.tasks[0].recurrence,
      recursNextAt: props.tasks[0].recursNextAt,
    };
  }
  return { recurrence: null, recursNextAt: null };
});
const selectedStatusDuid = computed(() => {
  if (!isDefault.value || isReadOnly.value || isFormOrAutomationMode.value) {
    return null;
  }
  return props.tasks.every((task) => task.statusDuid === props.tasks[0].statusDuid) ? props.tasks[0].statusDuid : null;
});

const hasValue = computed(
  () =>
    (isRange.value && selectedDateRange.value.start) ||
    selectedDateRange.value.end ||
    selectedRecurrence.value.recurrence
);

const generateRecommendation = async () => {
  const tempSelectedTasks = selectedTaskDuids.value;
  if (tempSelectedTasks.length > 100) {
    notify({ message: "Dart AI cannot fill out more than 100 tasks at once", type: NotificationType.ERROR });
    return;
  }

  appStore.startLoadingAiTaskProperties(tempSelectedTasks, props.property.duid);
  const [taskUpdates, recDuids] = await generateFieldRecommendation(selectedTasks.value, "dueAt", null);

  dataStore.updateTasks(taskUpdates);
  appStore.finishLoadingAiTaskProperties(tempSelectedTasks, props.property.duid);

  actions.visualization.showFeedbackTooltipForTask(
    tempSelectedTasks,
    recDuids,
    props.editorMode,
    feedbackTooltipNode.value ?? null
  );
};

const updateTaskDates = (startAt: string | null, dueAt: string | null) => {
  emit("update", [startAt, dueAt]);
  if (isFormOrAutomationMode.value) {
    return;
  }

  dataStore.updateTasks(
    selectedTasks.value.map((task) => ({
      duid: task.duid,
      ...getPropertyPartialTask(props.property, task, [isRange.value ? startAt : selectedDateRange.value.start, dueAt]),
    }))
  );
};

const subtasks = computed(() =>
  props.tasks[0]
    ? dataStore.getTasksByDuidsOrderedIfLoaded(dataStore.getDescendantDuidsUiOnly(props.tasks[0]), {
        includeTrashed: false,
        includeDraft: false,
      })
    : []
);

const hasSubtaskDates = computed(() =>
  subtasks.value.some((task) => {
    const [start, end] = getPropertyValueFromTask(props.property, task) ?? [];
    return start || end;
  })
);

const rejectRecommendation = () => {
  updateTaskDates(null, null);
  emit("update");
  emit("reject");
};

const handleAfterClose = () => {
  props.onAfterClose?.([selectedDateRange.value.start, selectedDateRange.value.end]);
};

onUnmounted(() => {
  appStore.showFeedbackTooltip(null, []);
});

defineExpose({
  open: () => datePicker.value?.open(),
  isOpen: computed(() => datePicker.value?.isOpen),
});
</script>

<template>
  <DatePicker
    ref="datePicker"
    :disabled="isReadOnly"
    :value="isRange ? selectedDateRange : (selectedDateRange.end ?? null)"
    :block="isListMode || isFormOrAutomationMode"
    :height-block="isListMode || isFormOrAutomationMode"
    :skidding="isFormOrAutomationMode ? -1 : undefined"
    :distance="isFormOrAutomationMode ? 2 : undefined"
    :editor-mode="editorMode"
    :show-recommendation-button="propertyConfig.isDefault && !isFormOrAutomationMode"
    :hide-actions="!propertyConfig.isDefault"
    :property-title="getPropertyDisplayTitle(property)"
    :cover="!isContextMenuMode && !isFormOrAutomationMode"
    :placement="isContextMenuMode ? Placement.RIGHT_TOP : Placement.BOTTOM_LEFT"
    :on-after-close="handleAfterClose"
    @select="(date) => updateTaskDates(null, date)"
    @select-range="(dateRange) => updateTaskDates(dateRange.start ?? null, dateRange.end ?? null)"
    @recommend="generateRecommendation">
    <DropdownMenuItemContent
      v-if="isContextMenuMode"
      :icon="DatesFieldIcon"
      :title="`Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
      is-submenu />
    <Tooltip
      v-else
      :disabled="isFormOrAutomationMode"
      :command-ids="
        isDefault && !isReadOnly
          ? [...(isRange ? [CommandId.CHANGE_START_DATE] : []), CommandId.CHANGE_DUE_DATE]
          : undefined
      "
      :text="
        isReadOnly
          ? `${getPropertyDisplayTitle(property)}${selectedDateRange.end !== null ? ` ${getFullDate(moment(selectedDateRange.end))}` : ''}`
          : isDefault
            ? undefined
            : `Change ${getPropertyDisplayTitle(property).toLowerCase()}`
      "
      :block="isListMode || isFormOrAutomationMode"
      :height-block="isListMode || isFormOrAutomationMode"
      :class="isTaskDetailMode && 'w-full'">
      <div
        ref="feedbackTooltipNode"
        :class="['group/dates relative flex size-full items-center', !isTaskDetailMode && !isChipMode && 'px-2.5']">
        <DatesChip
          :task="isFormOrAutomationMode ? undefined : props.tasks[0]"
          :property="property"
          :value="[selectedDateRange.start, selectedDateRange.end]"
          :status-duid="selectedStatusDuid"
          :recurrence="isDefault ? selectedRecurrence.recurrence : null"
          :loading="loading"
          :show-if-empty="isTaskDetailMode || isChipMode || isFormOrAutomationMode || loading"
          :show-icon="showIcon"
          :editor-mode="editorMode"
          @accept="emit('update')"
          @reject="rejectRecommendation" />
        <div
          v-if="isListMode && !hasValue && !hasSubtaskDates"
          class="flex size-full items-center justify-center text-transparent group-hover/dates:text-vlt">
          <component :is="isRange ? DatesFieldIcon : DueDateFieldIcon" class="icon-xs" />
        </div>
      </div>
    </Tooltip>
  </DatePicker>
</template>
